export const globalTokens = {
  color: {
    purple50: '#F5EEFA',
    purple100: '#EBDCF6',
    purple200: '#D9B8EC',
    purple300: '#BE86DF',
    purple400: '#A559D0',
    purple500: '#8B35BE',
    purple600: '#6F1F98',
    purple700: '#53166F',
    purple800: '#3C0F52',
    purple900: '#2B0B3E',
    gold50: '#FFF9E6',
    gold100: '#FFF0BF',
    gold200: '#FFE38A',
    gold300: '#FFD652',
    gold400: '#FAC508',
    gold500: '#E0B307',
    gold600: '#BE9600',
    gold700: '#8D6E00',
    gold800: '#5C4800',
    gold900: '#3B2E00',
    // Third place. The palette had gold and it had neutrals for silver, and
    // nothing that could stand in for bronze without reading as a warning.
    bronze200: '#E8B48A',
    bronze500: '#C27A3E',
    bronze700: '#8A5223',
    pink300: '#E5A5F6',
    pink400: '#CF6EF0',
    pink500: '#B839E1',
    neutral0: '#FFFFFF',
    neutral50: '#F8FAFC',
    neutral100: '#F1F5F9',
    neutral200: '#E2E8F0',
    neutral300: '#CBD5E1',
    neutral350: '#9FACBF',
    neutral400: '#94A3B8',
    neutral500: '#64748B',
    neutral600: '#475569',
    neutral700: '#334155',
    neutral800: '#1E293B',
    neutral900: '#0F172A',
    success100: '#DCFCE7',
    success500: '#22C55E',
    success600: '#14B87E',
    success700: '#15803D',
    warning100: '#FEF3C7',
    warning500: '#F59E0B',
    warning700: '#B45309',
    error100: '#FEE2E2',
    error400: '#F37171',
    error500: '#EF4444',
    error700: '#B91C1C',
    info100: '#DBEAFE',
    info400: '#669EF8',
    info500: '#3B82F6',
    info700: '#1D4ED8'
  },
  spacing: {
    xxs: '0.125rem',
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.5rem',
    xxl: '2rem',
    xxxl: '3rem'
  },
  radius: {
    xs: '0.25rem',
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    pill: '999px'
  },
  shadow: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.08)',
    md: '0 6px 14px rgba(15, 23, 42, 0.12)',
    lg: '0 18px 36px rgba(15, 23, 42, 0.18)',
    focusBrand: '0 0 0 3px rgba(250, 197, 8, 0.35)',
    focusInfo: '0 0 0 3px rgba(59, 130, 246, 0.22)'
  },
  motion: {
    fast: '120ms',
    normal: '200ms'
  },
  font: {
    familyBody: "'Nunito Sans', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    familyDisplay: "'Nunito Sans', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    familyMono: "'Cascadia Code', 'Fira Code', Consolas, monospace"
  }
};

export const semanticTokens = {
  // Dark theme: semantic slots point to the dark end of globalTokens.color's
  // neutral/status ramps (the raw ramps themselves are unchanged, so anything
  // reading globalTokens.color.* directly is unaffected by this file).
  color: {
    surface: globalTokens.color.neutral800,
    surfaceMuted: globalTokens.color.neutral900,
    surfaceSubtle: '#161d2c',
    surfaceRaised: globalTokens.color.neutral700,
    surfaceOnVivid: 'rgba(15, 23, 42, 0.72)',
    surfaceBrandSubtle: globalTokens.color.purple900,
    surfaceInfoSubtle: 'rgba(59, 130, 246, 0.14)',
    surfaceWarningSubtle: 'rgba(245, 158, 11, 0.14)',
    textDefault: globalTokens.color.neutral50,
    textSubtle: globalTokens.color.neutral300,
    textMuted: globalTokens.color.neutral350,
    textDisabled: globalTokens.color.neutral600,
    textInverse: globalTokens.color.neutral900,
    textOnVivid: globalTokens.color.neutral0,
    textOnVividMuted: 'rgba(255, 255, 255, 0.88)',
    textBrand: globalTokens.color.purple200,
    borderSubtle: globalTokens.color.neutral700,
    borderDefault: globalTokens.color.neutral600,
    borderStrong: globalTokens.color.neutral500,
    borderFocus: globalTokens.color.purple400,
    borderBrand: globalTokens.color.purple500,
    actionPrimaryBg: globalTokens.color.gold400,
    actionPrimaryText: globalTokens.color.purple900,
    actionSecondaryBg: globalTokens.color.purple600,
    actionSecondaryBgHover: globalTokens.color.purple700,
    statusSuccessBg: 'rgba(34, 197, 94, 0.14)',
    statusSuccessText: globalTokens.color.success500,
    statusWarningBg: 'rgba(245, 158, 11, 0.14)',
    statusWarningText: globalTokens.color.warning500,
    statusErrorBg: 'rgba(239, 68, 68, 0.14)',
    statusErrorText: globalTokens.color.error400,
    statusInfoBg: 'rgba(59, 130, 246, 0.14)',
    statusSuccessAccent: globalTokens.color.success600,
    surfaceSuccessDeep: '#0D2920',
    surfaceWarningDeep: '#1C1608',
    surfaceErrorDeep: '#2A0E0E',
    statusNeutralBg: globalTokens.color.neutral700,
    statusNeutralText: globalTokens.color.neutral300,
    statusBrandBg: 'rgba(139, 53, 190, 0.18)',
    statusBrandText: globalTokens.color.purple200,
    statusInfoText: globalTokens.color.info400,
    focusRing: globalTokens.shadow.focusBrand
  },
  button: {
    primary: {
      solid: {
        bg: globalTokens.color.gold400,
        bgHover: globalTokens.color.gold500,
        bgActive: globalTokens.color.gold600,
        bgDisabled: globalTokens.color.neutral700,
        text: globalTokens.color.purple900,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.gold400,
        borderHover: globalTokens.color.gold500,
        borderActive: globalTokens.color.gold600,
        borderFocus: globalTokens.color.pink500,
        borderDisabled: globalTokens.color.neutral700
      },
      outline: {
        bg: 'transparent',
        bgHover: 'rgba(250, 197, 8, 0.12)',
        bgActive: 'rgba(250, 197, 8, 0.2)',
        bgDisabled: globalTokens.color.neutral800,
        text: globalTokens.color.gold300,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.purple400,
        borderHover: globalTokens.color.purple500,
        borderActive: globalTokens.color.purple600,
        borderFocus: globalTokens.color.pink500,
        borderDisabled: globalTokens.color.neutral700
      },
      text: {
        bg: 'transparent',
        bgHover: 'rgba(139, 53, 190, 0.16)',
        bgActive: 'rgba(139, 53, 190, 0.26)',
        bgDisabled: 'transparent',
        text: globalTokens.color.purple300,
        textDisabled: globalTokens.color.neutral500,
        border: 'transparent',
        borderHover: 'transparent',
        borderActive: 'transparent',
        borderFocus: globalTokens.color.pink500,
        borderDisabled: 'transparent'
      }
    },
    secondary: {
      solid: {
        bg: globalTokens.color.purple600,
        bgHover: globalTokens.color.purple700,
        bgActive: globalTokens.color.purple800,
        bgDisabled: globalTokens.color.neutral700,
        text: globalTokens.color.neutral0,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.purple600,
        borderHover: globalTokens.color.purple700,
        borderActive: globalTokens.color.purple800,
        borderFocus: globalTokens.color.pink500,
        borderDisabled: globalTokens.color.neutral700
      },
      outline: {
        bg: 'transparent',
        bgHover: 'rgba(139, 53, 190, 0.16)',
        bgActive: 'rgba(139, 53, 190, 0.26)',
        bgDisabled: globalTokens.color.neutral800,
        text: globalTokens.color.purple300,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.purple300,
        borderHover: globalTokens.color.purple400,
        borderActive: globalTokens.color.purple500,
        borderFocus: globalTokens.color.pink500,
        borderDisabled: globalTokens.color.neutral700
      },
      text: {
        bg: 'transparent',
        bgHover: 'rgba(139, 53, 190, 0.16)',
        bgActive: 'rgba(139, 53, 190, 0.26)',
        bgDisabled: 'transparent',
        text: globalTokens.color.purple300,
        textDisabled: globalTokens.color.neutral500,
        border: 'transparent',
        borderHover: 'transparent',
        borderActive: 'transparent',
        borderFocus: globalTokens.color.pink500,
        borderDisabled: 'transparent'
      }
    },
    neutral: {
      solid: {
        bg: globalTokens.color.neutral700,
        bgHover: globalTokens.color.neutral600,
        bgActive: globalTokens.color.neutral500,
        bgDisabled: globalTokens.color.neutral800,
        text: globalTokens.color.neutral0,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.neutral700,
        borderHover: globalTokens.color.neutral600,
        borderActive: globalTokens.color.neutral500,
        borderFocus: globalTokens.color.purple400,
        borderDisabled: globalTokens.color.neutral800
      },
      outline: {
        bg: 'transparent',
        bgHover: globalTokens.color.neutral800,
        bgActive: globalTokens.color.neutral700,
        bgDisabled: globalTokens.color.neutral800,
        text: globalTokens.color.neutral300,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.neutral600,
        borderHover: globalTokens.color.neutral500,
        borderActive: globalTokens.color.neutral400,
        borderFocus: globalTokens.color.purple400,
        borderDisabled: globalTokens.color.neutral700
      },
      text: {
        bg: 'transparent',
        bgHover: globalTokens.color.neutral800,
        bgActive: globalTokens.color.neutral700,
        bgDisabled: 'transparent',
        text: globalTokens.color.neutral300,
        textDisabled: globalTokens.color.neutral500,
        border: 'transparent',
        borderHover: 'transparent',
        borderActive: 'transparent',
        borderFocus: globalTokens.color.purple400,
        borderDisabled: 'transparent'
      }
    },
    danger: {
      solid: {
        bg: globalTokens.color.error500,
        bgHover: globalTokens.color.error700,
        bgActive: '#7F1D1D',
        bgDisabled: globalTokens.color.neutral700,
        text: globalTokens.color.neutral0,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.error500,
        borderHover: globalTokens.color.error700,
        borderActive: '#7F1D1D',
        borderFocus: globalTokens.color.error500,
        borderDisabled: globalTokens.color.neutral700
      },
      outline: {
        bg: 'transparent',
        bgHover: 'rgba(239, 68, 68, 0.14)',
        bgActive: 'rgba(239, 68, 68, 0.24)',
        bgDisabled: globalTokens.color.neutral800,
        text: globalTokens.color.error500,
        textDisabled: globalTokens.color.neutral500,
        border: globalTokens.color.error500,
        borderHover: globalTokens.color.error700,
        borderActive: '#7F1D1D',
        borderFocus: globalTokens.color.error500,
        borderDisabled: globalTokens.color.neutral700
      },
      text: {
        bg: 'transparent',
        bgHover: 'rgba(239, 68, 68, 0.14)',
        bgActive: 'rgba(239, 68, 68, 0.24)',
        bgDisabled: 'transparent',
        text: globalTokens.color.error500,
        textDisabled: globalTokens.color.neutral500,
        border: 'transparent',
        borderHover: 'transparent',
        borderActive: 'transparent',
        borderFocus: globalTokens.color.error500,
        borderDisabled: 'transparent'
      }
    }
  },
  field: {
    bg: globalTokens.color.neutral800,
    text: globalTokens.color.neutral50,
    placeholder: globalTokens.color.neutral500,
    description: globalTokens.color.neutral400,
    stroke: globalTokens.color.neutral600,
    strokeHover: globalTokens.color.neutral500,
    strokeFocus: globalTokens.color.purple400,
    strokeValidation: globalTokens.color.warning500,
    strokeLoading: globalTokens.color.info500,
    strokeSuccess: globalTokens.color.success500,
    strokeError: globalTokens.color.error500,
    strokeDisabled: globalTokens.color.neutral700,
    bgValidation: 'rgba(245, 158, 11, 0.1)',
    bgLoading: 'rgba(59, 130, 246, 0.1)',
    bgSuccess: 'rgba(34, 197, 94, 0.1)',
    bgError: 'rgba(239, 68, 68, 0.1)',
    bgDisabled: globalTokens.color.neutral900
  },
  control: {
    checkbox: {
      size: '16px',
      radius: globalTokens.radius.xs,
      stroke: globalTokens.color.neutral500,
      strokeHover: globalTokens.color.purple400,
      strokeFocus: globalTokens.color.pink500,
      strokeDisabled: globalTokens.color.neutral700,
      strokeError: globalTokens.color.error500,
      bg: globalTokens.color.neutral800,
      bgChecked: globalTokens.color.purple500,
      bgDisabled: globalTokens.color.neutral900,
      icon: globalTokens.color.neutral0,
      iconDisabled: globalTokens.color.neutral600
    },
    radio: {
      size: '16px',
      dotSize: '6px',
      stroke: globalTokens.color.neutral500,
      strokeHover: globalTokens.color.purple400,
      strokeDisabled: globalTokens.color.neutral700,
      strokeError: globalTokens.color.error500,
      bg: globalTokens.color.neutral800,
      dot: globalTokens.color.purple400,
      dotDisabled: globalTokens.color.neutral600
    },
    toggle: {
      width: '24px',
      height: '14px',
      thumb: '10px',
      trackOff: globalTokens.color.neutral600,
      trackHover: globalTokens.color.neutral500,
      trackOn: globalTokens.color.purple500,
      trackOnHover: globalTokens.color.purple400,
      trackDisabled: globalTokens.color.neutral800,
      thumbColor: globalTokens.color.neutral0,
      thumbDisabled: globalTokens.color.neutral600
    }
  },
  icon: {
    sizeSm: '20px',
    sizeMd: '24px',
    sizeLg: '32px',
    sizeXl: '40px',
    colorDefault: globalTokens.color.neutral300,
    colorMuted: globalTokens.color.neutral500,
    colorBrand: globalTokens.color.purple300,
    colorInverse: globalTokens.color.neutral900,
    colorSuccess: globalTokens.color.success500,
    colorWarning: globalTokens.color.warning500,
    colorDanger: globalTokens.color.error500,
    colorInfo: globalTokens.color.info500,
    stroke: '2'
  },
  /* Back navigation is chromeless by default: white text over whatever the page
     background is, carrying its own shadow so it stays legible on un-scrimmed hero
     photos without needing a pill behind it. `subtle` is the muted variant for back
     links sitting inline on a surface, where white would over-weight the text
     around it. */
  backNav: {
    textPlain: globalTokens.color.neutral0,
    textPlainHover: globalTokens.color.purple300,
    shadowPlain: '0 1px 6px rgba(0, 0, 0, 0.75)',
    iconShadowPlain: '0 1px 4px rgba(0, 0, 0, 0.85)',
    textSubtle: globalTokens.color.neutral400,
    textSubtleHover: globalTokens.color.purple300
  },
  accordion: {
    itemBg: globalTokens.color.neutral800,
    itemBgHover: globalTokens.color.neutral700,
    itemBgOpen: globalTokens.color.neutral700,
    itemBorder: globalTokens.color.neutral700,
    summaryText: globalTokens.color.neutral50,
    summarySubtle: globalTokens.color.neutral400
  },
  backgroundMode: {
    vivid: 'linear-gradient(135deg, #1a0628 0%, #13151f 55%, #0c0d14 100%)',
    calm: 'linear-gradient(135deg, #13151f 0%, #1a1d2a 100%)',
    contrast: 'linear-gradient(145deg, #0c0d14 0%, #13151f 50%, #0a0b10 100%)'
  },
  card: {
    elevated: {
      bg: globalTokens.color.neutral800,
      border: globalTokens.color.neutral700,
      shadow: globalTokens.shadow.sm
    },
    outlined: {
      bg: globalTokens.color.neutral800,
      border: globalTokens.color.neutral600,
      shadow: 'none'
    },
    background: {
      default: globalTokens.color.neutral800,
      subtle: globalTokens.color.neutral900,
      brand: globalTokens.color.purple900,
      success: 'rgba(34, 197, 94, 0.14)',
      warning: 'rgba(245, 158, 11, 0.14)',
      error: 'rgba(239, 68, 68, 0.14)'
    },
    borderVariant: {
      none: 'transparent',
      subtle: globalTokens.color.neutral700,
      strong: globalTokens.color.neutral500,
      accent: globalTokens.color.purple400,
      success: globalTokens.color.success500,
      warning: globalTokens.color.warning500,
      error: globalTokens.color.error500
    },
    corner: {
      square: '0px',
      sm: globalTokens.radius.sm,
      md: globalTokens.radius.md,
      lg: globalTokens.radius.lg,
      xl: globalTokens.radius.xl
    },
    tonal: {
      bg: globalTokens.color.purple900,
      border: globalTokens.color.purple700,
      shadow: 'none'
    },
    // The bracketed card: a header strip over the body, and a footer strip under it.
    // Both strips sit on the same subtle surface with a rule against the body, which is
    // what makes the card read as a bounded block rather than content that starts and
    // stops. Values are what the two hand-rolled implementations (My Membership's Panel
    // and Manage User's mu-card) had independently converged on, moved onto the scale:
    // their 14px horizontal padding becomes spacing.lg (16px).
    //
    // bg is the literal rather than a reference because semanticTokens cannot refer to
    // itself while it is being defined, and to-dtcg.mjs only understands literals and
    // member expressions. It must stay equal to color.surfaceSubtle above.
    header: {
      bg: '#161d2c',
      border: globalTokens.color.neutral700,
      paddingY: globalTokens.spacing.md,
      paddingX: globalTokens.spacing.lg
    },
    body: {
      padding: globalTokens.spacing.lg,
      paddingComfortable: globalTokens.spacing.xl
    },
    footer: {
      bg: '#161d2c',
      border: globalTokens.color.neutral700,
      paddingY: globalTokens.spacing.md,
      paddingX: globalTokens.spacing.lg,
      // Tall enough to be a comfortable phone target on its own, since the footer bar is
      // frequently the card's only control.
      minHeight: '48px',
      actionText: globalTokens.color.purple200,
      actionBgHover: globalTokens.color.neutral700
    }
  },
  countIndicator: {
    bg: globalTokens.color.error500,
    text: globalTokens.color.neutral0,
    brandBg: globalTokens.color.gold400,
    brandText: globalTokens.color.purple900,
    size: '18px',
    ringColor: globalTokens.color.neutral900
  },
  sheet: {
    overlay: 'rgba(6, 7, 12, 0.65)',
    bg: globalTokens.color.neutral800,
    grabber: globalTokens.color.neutral600,
    radius: globalTokens.radius.xl,
    maxWidth: '480px'
  },
  modal: {
    backdrop: 'rgba(6, 7, 12, 0.65)',
    border: globalTokens.color.neutral700,
    sizeSm: '420px',
    sizeMd: '560px',
    sizeLg: '760px'
  },
  splitButton: {
    menuBg: globalTokens.color.neutral800,
    menuBorder: globalTokens.color.neutral700,
    menuShadow: globalTokens.shadow.md,
    menuItemHover: globalTokens.color.neutral700
  },
  filterSelect: {
    chipBg: globalTokens.color.purple900,
    chipText: globalTokens.color.purple300,
    chipBorder: globalTokens.color.purple700,
    panelBg: globalTokens.color.neutral800,
    panelBorder: globalTokens.color.neutral700,
    panelShadow: globalTokens.shadow.md
  },
  spacing: {
    controlY: globalTokens.spacing.md,
    controlX: globalTokens.spacing.lg,
    sectionGap: globalTokens.spacing.xl,
    cardPadding: globalTokens.spacing.xl,
    iconGap: globalTokens.spacing.sm
  },
  radius: {
    control: globalTokens.radius.md,
    card: globalTokens.radius.lg,
    badge: globalTokens.radius.pill,
    button: globalTokens.radius.md
  },
  shadow: {
    card: globalTokens.shadow.sm,
    modal: globalTokens.shadow.lg,
    focusInfo: globalTokens.shadow.focusInfo
  },
  font: {
    body: globalTokens.font.familyBody,
    display: globalTokens.font.familyDisplay,
    mono: globalTokens.font.familyMono,
    sizeBody: '1rem',
    sizeBodySmall: '0.875rem',
    sizeLabel: '0.875rem',
    sizeTitle: '1.25rem'
  },
  typography: {
    heading: {
      desktop: {
        h1: { size: '3.5rem', lineHeight: '1.1', weight: '800' },
        h2: { size: '2.5rem', lineHeight: '1.15', weight: '800' },
        h3: { size: '1.875rem', lineHeight: '1.2', weight: '700' },
        h4: { size: '1.5rem', lineHeight: '1.2', weight: '700' },
        h5: { size: '1.25rem', lineHeight: '1.2', weight: '700' }
      },
      tablet: {
        h1: { size: '3rem', lineHeight: '1.1', weight: '800' },
        h2: { size: '2.25rem', lineHeight: '1.15', weight: '800' },
        h3: { size: '1.75rem', lineHeight: '1.2', weight: '700' },
        h4: { size: '1.375rem', lineHeight: '1.2', weight: '700' },
        h5: { size: '1.125rem', lineHeight: '1.2', weight: '700' }
      },
      mobile: {
        h1: { size: '2rem', lineHeight: '1.2', weight: '800' },
        h2: { size: '1.5rem', lineHeight: '1.2', weight: '700' },
        h3: { size: '1.25rem', lineHeight: '1.2', weight: '700' },
        h4: { size: '1rem', lineHeight: '1.2', weight: '700' },
        h5: { size: '0.875rem', lineHeight: '1.2', weight: '700' }
      }
    },
    body: {
      desktop: {
        body1: { size: '1rem', lineHeight: '1.4', weight: '400' },
        body2: { size: '1rem', lineHeight: '1.3', weight: '400' },
        sm: { size: '0.875rem', lineHeight: '1.4', weight: '400' },
        xs: { size: '0.75rem', lineHeight: '1.4', weight: '400' }
      },
      tablet: {
        body1: { size: '1rem', lineHeight: '1.3', weight: '400' },
        body2: { size: '0.875rem', lineHeight: '1.3', weight: '400' },
        sm: { size: '0.875rem', lineHeight: '1.4', weight: '400' },
        xs: { size: '0.75rem', lineHeight: '1.4', weight: '400' }
      },
      mobile: {
        body1: { size: '1rem', lineHeight: '1.3', weight: '400' },
        body2: { size: '0.875rem', lineHeight: '1.3', weight: '400' },
        sm: { size: '0.8125rem', lineHeight: '1.4', weight: '400' },
        xs: { size: '0.6875rem', lineHeight: '1.4', weight: '400' }
      }
    },
    button: {
      large: { size: '1.125rem', lineHeight: '1.2', weight: '700' },
      normal: { size: '1rem', lineHeight: '1.2', weight: '700' },
      medium: { size: '0.875rem', lineHeight: '1.2', weight: '700' },
      small: { size: '0.8125rem', lineHeight: '1.2', weight: '700' },
      xsmall: { size: '0.75rem', lineHeight: '1.15', weight: '700' }
    },
    field: {
      placeholder: { size: '1rem', lineHeight: '1.2', weight: '400' },
      text: { size: '1rem', lineHeight: '1.2', weight: '400' },
      description: { size: '0.75rem', lineHeight: '1.3', weight: '400' }
    },
    // A compact card header labels the rows beneath it, so it is set as a label rather
    // than as a heading: smaller and heavier than h5, which is the nearest heading step
    // and reads as a section title the body then has to live up to.
    label: {
      card: { size: '0.8125rem', lineHeight: '1.2', weight: '800' },
      cardSubtitle: { size: '0.75rem', lineHeight: '1.45', weight: '400' }
    }
  },
  motion: {
    interactive: globalTokens.motion.fast
  }
};

const flattenObject = (obj, prefix = '') => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const tokenName = prefix ? `${prefix}-${key}` : key;
    if (typeof value === 'object' && value !== null) {
      Object.assign(acc, flattenObject(value, tokenName));
    } else {
      acc[tokenName] = String(value);
    }
    return acc;
  }, {});
};

export const toCssVariables = (tokens, prefix) => {
  const flattened = flattenObject(tokens);
  return Object.entries(flattened)
    .map(([key, value]) => `--${prefix}-${key}: ${value};`)
    .join('\n');
};

export const designSystemCssVariables = `:root {\n${toCssVariables(globalTokens, 'ds-global')}\n${toCssVariables(semanticTokens, 'ds')}\n}`;
